// Fuzzy citation-highlight engine, shared by the PDF overlay and the text
// preview in the source drawer.
//
// WHY FUZZY: a citation's `quote` is the retrieved chunk text as the model
// TRANSCRIBED it during ingestion — whitespace-normalized, occasionally
// paraphrased around figures, and sometimes describing a chart rather than
// quoting printed words. Exact substring search against the document would
// miss constantly, so matching is token-overlap scoring instead: a candidate
// piece of document text (a PDF text item, a line of a text file) matches
// when most of ITS significant tokens appear in the quotes' token set, or
// when it shares an identifier-like token (part numbers, page refs, timing
// values) with a quote.
//
// Everything in this module is pure and DOM-free so it can be unit tested
// directly.

/** An item matches when this fraction of its significant tokens appear in the quote set. */
export const SCORE_THRESHOLD = 0.6;
/** ...and it carries at least this many significant tokens (guards trivial one-word hits). */
export const MIN_SIGNIFICANT_TOKENS = 2;

// Provenance markers the ingestion pipeline embeds verbatim into chunk text
// (mirrors backend KnowledgeBoundary constants). They describe HOW a value
// was read, never document content, so they must not contribute match tokens.
const PROVENANCE_MARKERS = [
    "(read from chart axis)",
    "values shown graphically, not printed",
    "too small to read",
    "(observed from product photograph)",
    "(read from fine print)",
];

// Deliberately tiny: only glue words common enough to make unrelated lines
// look similar. Domain words stay in — they are exactly the evidence we score.
const STOPWORDS = new Set([
    "the", "and", "for", "are", "was", "were", "has", "have", "had",
    "this", "that", "these", "those", "with", "from", "not", "its",
    "their", "which", "will", "can", "may", "been", "also", "than",
    "then", "into", "over", "under", "such", "but", "all", "any",
    "per", "via", "you", "your", "our", "out", "when", "where", "what",
]);

// "Document notes" aggregate chunks prefix each note line with a page run
// like "p17", "p18-19" or "p4, p9". The prefix locates the note, it is not
// document text, so it is stripped before matching. The trailing whitespace
// requirement keeps tokens that merely START with p+digits (e.g. "p17-rev2"
// as a full identifier line) intact when nothing follows them.
const PAGE_RUN_PREFIX = /^\s*p\d+(?:\s*-\s*p?\d+)?(?:\s*,\s*p\d+(?:\s*-\s*p?\d+)?)*[:.–—-]?\s+/i;

// Structural markers BasicDocumentTextExtractor writes into extracted text
// ("[Page 3]", "[Slide 2]", "[Table]", "[Sheet: Q3]", ...). The prompts tell
// the ingestion model to omit them, but nothing deterministically strips
// them, and the offline stub's verbatim chunks keep them — so they can leak
// into quotes. They locate content, they aren't content: no match tokens.
const EXTRACTOR_MARKERS = /\[(?:page|slide)\s+\d+\]|\[(?:end\s+)?table(?::[^\]\n]*)?\]|\[sheet:[^\]\n]*\]|\[section:[^\]\n]*\]|\[(?:end\s+)?spatial table column \d+\]/gi;

const DOCUMENT_NOTES_FIRST_LINE = "Document notes";

/** Lowercase + collapse all whitespace runs to single spaces. */
export function normalize(text: string): string {
    return text.toLowerCase().replace(/\s+/g, " ").trim();
}

/**
 * Removes ingestion provenance markers, and for "Document notes" aggregate
 * chunks removes the header line and each line's page-run prefix. The
 * returned text is only ever fed to the tokenizer — it is never displayed.
 */
export function stripProvenanceMarkers(quote: string): string {
    let result = quote.replace(EXTRACTOR_MARKERS, " ");
    for (const marker of PROVENANCE_MARKERS) {
        result = result.split(marker).join(" ");
    }

    const lines = result.split(/\r\n|\r|\n/);
    if (lines.length > 0 && lines[0].trim() === DOCUMENT_NOTES_FIRST_LINE) {
        result = lines
            .slice(1)
            .map((line) => line.replace(PAGE_RUN_PREFIX, ""))
            .join("\n");
    }

    return result;
}

/**
 * Identifier-like tokens are the strongest possible evidence a piece of text
 * relates to a quote (part numbers, signal names, timing values), so they are
 * always significant and can match on their own. Bare 1–3 digit numbers are
 * deliberately NOT identifier-like — "30" appears on every page of a
 * datasheet and would highlight indiscriminately.
 */
export function isIdentifierToken(token: string): boolean {
    if (/\d/.test(token) && /[a-z]/i.test(token)) return true; // 10ns, spi-1042, p17, 0x3f
    if (/^[a-z0-9]+(?:-[a-z0-9]+)+$/i.test(token) && /\d/.test(token)) return true; // 18-19, rev-2
    if (/^\d{4,}$/.test(token)) return true; // long numerics: 1042, 2026
    if (/^[a-z]+(?:-[a-z]+)+$/i.test(token)) return true; // clock-phase style compound names
    return false;
}

/**
 * Tokens worth scoring: length ≥ 3 minus the stopword set, plus
 * identifier-like tokens regardless of length. Input need not be
 * pre-normalized.
 */
export function significantTokens(text: string): string[] {
    return normalize(text)
        .split(/[^a-z0-9-]+/)
        .map((token) => token.replace(/^-+|-+$/g, ""))
        .filter(Boolean)
        .filter((token) => isIdentifierToken(token) || (token.length >= 3 && !STOPWORDS.has(token)));
}

/**
 * Fraction of the item's significant tokens present in the quote token set.
 * 0 when the item has no significant tokens at all.
 */
export function scoreTextItem(itemText: string, quoteTokenSet: Set<string>): number {
    const tokens = significantTokens(itemText);
    if (tokens.length === 0) return 0;
    let hits = 0;
    for (const token of tokens) {
        if (quoteTokenSet.has(token)) hits += 1;
    }
    return hits / tokens.length;
}

export type QuoteTokenSets = {
    /** Every significant token across all quotes (markers stripped first). */
    all: Set<string>;
    /** The identifier-like subset — eligible for the single-token match rule. */
    identifiers: Set<string>;
};

export function buildQuoteTokenSets(quotes: string[]): QuoteTokenSets {
    const all = new Set<string>();
    const identifiers = new Set<string>();
    for (const quote of quotes) {
        for (const token of significantTokens(stripProvenanceMarkers(quote))) {
            all.add(token);
            if (isIdentifierToken(token)) identifiers.add(token);
        }
    }
    return { all, identifiers };
}

export type MatchOptions = {
    /**
     * When false, the "item contains an identifier-like token from a quote"
     * rule is disabled and only the score threshold can match. Used for
     * quotes with a null pageNumber, which are allowed to match on any PDF
     * page but only on strong evidence.
     */
    identifierFallback?: boolean;
};

/**
 * Indices of `textItems` to highlight against `quotes`. An item matches when
 * it has ≥ MIN_SIGNIFICANT_TOKENS significant tokens and ≥ SCORE_THRESHOLD of
 * them appear in the quotes' token set, OR (unless disabled) when it contains
 * an identifier-like token that a quote also contains.
 */
export function matchesForPage(textItems: string[], quotes: string[], options?: MatchOptions): number[] {
    const identifierFallback = options?.identifierFallback !== false;
    const { all, identifiers } = buildQuoteTokenSets(quotes);
    if (all.size === 0) return [];

    const matched: number[] = [];
    textItems.forEach((itemText, index) => {
        const tokens = significantTokens(itemText);
        if (tokens.length === 0) return;

        let hits = 0;
        for (const token of tokens) {
            if (all.has(token)) hits += 1;
        }

        const thresholdMatch = tokens.length >= MIN_SIGNIFICANT_TOKENS
            && hits / tokens.length >= SCORE_THRESHOLD;
        const identifierMatch = identifierFallback
            && tokens.some((token) => identifiers.has(token));

        if (thresholdMatch || identifierMatch) matched.push(index);
    });
    return matched;
}
